import { ValidationErrorResponseSchema } from '@mntn/api-contracts/_shared/error-responses'
import { ErrorResponseSchema } from '@mntn/result'
import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import {
  AddBudgetBodySchema,
  AddBudgetParamsSchema,
  AddBudgetRequestSchema,
  AddBudgetResponses,
  BaseQuerySchema,
  CreateCampaignExperimentInputSchema,
  CreateCampaignExperimentRequestSchema,
  CreateCampaignInputSchema,
  CreateCampaignRequestSchema,
  CreateCampaignResponseSchema,
  FindOrderItemsXCampaignGroupsRequestSchema,
  GetOrderItemByIdParamsSchema,
  GetOrderItemByIdRequestSchema,
  GetOrderItemByIdResponseSchema,
  GetOrderItemsErrorResponseSchema,
  GetOrderItemsStatusSummaryRequestSchema,
  GetOrderItemsSummaryRequestSchema,
  ListOrderItemsRequestSchema,
  OrderItemAdvertiserQuerySchema,
  OrderItemSummaryResponseSchema,
  OrderItemsGetRequestSchema,
  OrderItemsGetResponseSchema,
  OrderItemsStatusSummaryRequestSchema,
  OrderItemsStatusSummaryResponseSchema,
  OrderItemsSummaryRequestSchema,
  OrderItemsXCampaignGroupsQuerySchema,
  OrderItemsXCampaignGroupsResponseSchema,
} from './schemas.ts'

const c = initContract()

const BASE = '/flight-deck/v1/order-items'
const BadRequestResponseSchema = z.union([ValidationErrorResponseSchema, ErrorResponseSchema])

export const orderItemsContract = c.router(
  {
    list: c.query({
      method: 'GET',
      path: BASE,
      query: OrderItemsGetRequestSchema,
      request: ListOrderItemsRequestSchema,
      responses: {
        200: OrderItemsGetResponseSchema,
        500: GetOrderItemsErrorResponseSchema,
      },
    }),
    summary: c.query({
      method: 'GET',
      path: `${BASE}/summary`,
      query: OrderItemsSummaryRequestSchema,
      request: GetOrderItemsSummaryRequestSchema,
      responses: {
        200: OrderItemSummaryResponseSchema,
        500: GetOrderItemsErrorResponseSchema,
      },
    }),
    statusSummary: c.query({
      method: 'GET',
      path: `${BASE}/status-summary`,
      query: OrderItemsStatusSummaryRequestSchema,
      request: GetOrderItemsStatusSummaryRequestSchema,
      responses: {
        200: OrderItemsStatusSummaryResponseSchema,
      },
    }),
    campaignMappingsList: c.query({
      method: 'GET',
      path: `${BASE}/campaign-mappings`,
      description: 'Get order item campaign group mappings',
      query: OrderItemsXCampaignGroupsQuerySchema,
      request: FindOrderItemsXCampaignGroupsRequestSchema,
      responses: {
        200: OrderItemsXCampaignGroupsResponseSchema,
        500: ErrorResponseSchema,
      },
    }),
    createCampaign: c.mutation({
      method: 'POST',
      path: `${BASE}/:order-item-id/create-campaign`,
      pathParams: GetOrderItemByIdParamsSchema,
      query: OrderItemAdvertiserQuerySchema,
      body: CreateCampaignInputSchema,
      request: CreateCampaignRequestSchema,
      responses: {
        200: CreateCampaignResponseSchema,
        400: BadRequestResponseSchema,
        401: ErrorResponseSchema,
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
        422: ErrorResponseSchema,
        500: ErrorResponseSchema,
        502: ErrorResponseSchema,
        503: ErrorResponseSchema,
      },
    }),
    createCampaignExperiment: c.mutation({
      method: 'POST',
      path: `${BASE}/:order-item-id/create-campaign-experiment`,
      pathParams: GetOrderItemByIdParamsSchema,
      query: OrderItemAdvertiserQuerySchema,
      body: CreateCampaignExperimentInputSchema,
      request: CreateCampaignExperimentRequestSchema,
      responses: {
        200: CreateCampaignResponseSchema,
        400: BadRequestResponseSchema,
        401: ErrorResponseSchema,
        404: ErrorResponseSchema,
        409: ErrorResponseSchema,
        422: ErrorResponseSchema,
        500: ErrorResponseSchema,
        502: ErrorResponseSchema,
        503: ErrorResponseSchema,
      },
    }),
    getById: c.query({
      method: 'GET',
      path: `${BASE}/:order-item-id`,
      pathParams: GetOrderItemByIdParamsSchema,
      query: BaseQuerySchema,
      request: GetOrderItemByIdRequestSchema,
      responses: {
        200: GetOrderItemByIdResponseSchema,
        500: GetOrderItemsErrorResponseSchema,
      },
    }),
    addBudget: c.mutation({
      method: 'POST',
      path: `${BASE}/:order-item-id/add-budget`,
      pathParams: AddBudgetParamsSchema,
      body: AddBudgetBodySchema,
      request: AddBudgetRequestSchema,
      responses: AddBudgetResponses,
    }),
  },
  {
    strictStatusCodes: true,
    commonResponses: {
      400: ValidationErrorResponseSchema,
      403: ErrorResponseSchema,
    },
    metadata: { tags: ['Flight Deck'] },
  },
)

export type OrderItemsContract = typeof orderItemsContract

export { GET_ORDER_ITEMS_ERROR_RESPONSES } from './schemas.ts'
